import classNames from "classnames";
import upperFirst from "lodash/upperFirst";
import React from "react";
import { getAdvancingSessionProgress } from "advancing/utils";
import { PhosphorChatTeardropDots, PhosphorCheck, PhosphorHourglass, PhosphorIconProps, PhosphorPaperclip, PhosphorStack, PhosphorUsersThree, PhosphorX } from "components/Atoms/PhosphorIcon";
import { ProgressRing } from "components/Atoms/ProgressRing";
import { mapAppointmentDateTimeToShowDates } from "shared/utils/fieldsHelper/showFields";
import { formatShowTimeLabel, getShowAppointmentHeaderTexts } from "shared/utils/purpleHeaderUtils";
import { getShowTime } from "shared/utils/showDateTimeDisplay";
import { formatWrittenDayMonthYear } from "utilities";

interface Props {
  session: server.dto.AdvancingSession
  href: string
  logo?: JSX.Element
  footer?: JSX.Element
  side: "participant" | "approve"
}

export const AdvancingSessionCard = ({ session, href, logo, footer, side }: Props) => {
  const { show } = session;
  const texts = getShowAppointmentHeaderTexts(show);
  const showDates = mapAppointmentDateTimeToShowDates(show.dateTime);
  const timeLabel = formatShowTimeLabel(getShowTime(showDates));
  const unreadMessageCount = side === "participant" ? session.unreadMessageFromAgentCount : session.unreadMessageToAgentCount;

  return <div
    className="advancing-session-card card-surface-standalone hoverable"
    onClick={() => window.location.href = href}
  >
    <div className="advancing-session-card-show flex gap">
      {logo}
      <div className="advancing-session-card-info flex-column gap grow1">
        <div>
          {texts.tourName && <div className="text-sublabel">{texts.tourName}</div>}
          <div className="advancing-session-card-title text-ellipsis">{texts.title}</div>
          {texts.subtitle && <div className="advancing-session-card-subtitle">{texts.subtitle}</div>}
          {texts.location && <div className="text-sublabel">{texts.location}</div>}
        </div>
        <div className={classNames("flex align-center gap s1_small", { "text-light": !show.artistName })}>
          <PhosphorUsersThree />
          {show.artistName || RESX.Artist.NoArtistSet}
        </div>
      </div>
    </div>

    <div className="flex-column gap-mini">
      {showDates.mode === "hasTimeStartsInNight" && <div className="text-sublabel">{RESX.DateTime.InTheNightOf}</div>}
      <div className="advancing-session-card-subtitle">{formatWrittenDayMonthYear(showDates.startDisplayDate)}</div>
      {timeLabel && <div className="s1_small">{timeLabel}</div>}
    </div>

    <div className="advancing-session-card-counts">
      {side === "participant" && <CountPill iconComp={PhosphorHourglass} variant="yellow" count={session.mandatoryUnprovidedCount} title={RESX.Advancing.RequiredInformation} />}
      <CountPill iconComp={PhosphorStack} variant="purple" count={session.pendingCount} title={RESX.Advancing.PendingResponse} />
      {side === "participant" && <CountPill iconComp={PhosphorCheck} variant="green" count={session.approvedCount} title={RESX.Advancing.ApprovedResponse} />}
      <CountPill iconComp={PhosphorChatTeardropDots} variant="blue" count={unreadMessageCount} title={upperFirst(RESX.Message.newMessages)} />
      <CountPill iconComp={PhosphorX} variant="pink" count={session.declinedCount} title={RESX.Advancing.DeclinedResponse} />
      <CountPill iconComp={PhosphorPaperclip} variant="gray" count={session.filesCount} title={RESX.File.Files} />
    </div>

    <ProgressRing percentage={getAdvancingSessionProgress(session)} small />

    {footer && <div className="advancing-session-card-footer s1_small">{footer}</div>}
  </div>;
};

interface CountPillProps {
  iconComp: (props: PhosphorIconProps) => JSX.Element
  variant: "gray" | "yellow" | "green" | "blue" | "pink" | "purple"
  count?: number
  title: string
}

const CountPill = ({ iconComp: Icon, variant, count, title }: CountPillProps) => {
  if (count === 0) return null;

  return <div title={`${count} ${title}`} className={`flex align-center justify-center gap-mini advancing-count-pill advancing-count-pill-${variant}`}>
    <Icon iconSize="small" />
    {count !== undefined && <span>{count}</span>}
  </div>;
};
